if (!customElements.get('bbj-inspiration-library')) {
  class BBJInspirationLibrary extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-bbj-inspiration-card]')];
      this.groups = [...this.querySelectorAll('[data-filter-group]')];
      this.status = this.querySelector('[data-filter-status]');
      this.clearButton = this.querySelector('[data-clear-filters]');
      this.emptyState = this.querySelector('[data-filter-empty]');
      this.grid = this.querySelector('[data-inspiration-grid]');
      this.sortSelect = this.querySelector('[data-inspiration-sort]');
      this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
      this.loadMoreButton = this.querySelector('[data-load-more]');
      this.filters = { color: 'all', occasion: 'all', style: 'all' };
      this.visibleLimit = this.getBatchSize();

      this.groups.forEach((group) => {
        group.addEventListener('click', (event) => {
          const button = event.target.closest('[data-filter-value]');
          if (!button) return;
          const groupName = group.dataset.filterGroup;
          this.filters[groupName] = button.dataset.filterValue;
          group.querySelectorAll('[data-filter-value]').forEach((option) => {
            option.setAttribute('aria-pressed', String(option === button));
          });
          this.resetVisibleLimit();
          this.renderCards();
        });
      });

      this.clearButton?.addEventListener('click', () => this.resetFilters());
      this.sortSelect?.addEventListener('change', () => {
        this.applySort();
        this.resetVisibleLimit();
        this.renderCards();
      });
      this.loadMoreButton?.addEventListener('click', () => {
        this.visibleLimit += this.getBatchSize();
        this.renderCards();
      });
      window.addEventListener('resize', this.debounce(() => {
        const nextBatchSize = this.getBatchSize();
        if (nextBatchSize >= this.visibleLimit) {
          this.visibleLimit = nextBatchSize;
        }
        this.renderCards();
      }, 150));

      this.applySort();
      this.renderCards();
    }

    matchesValue(cardValue, filterValue) {
      if (filterValue === 'all') return true;
      return cardValue.split(/\s+/).filter(Boolean).includes(filterValue);
    }

    getBatchSize() {
      if (window.matchMedia('(max-width: 749px)').matches) return 12;
      if (window.matchMedia('(max-width: 989px)').matches) return 24;
      return 30;
    }

    resetVisibleLimit() {
      this.visibleLimit = this.getBatchSize();
    }

    getMatchingCards() {
      return this.cards.filter((card) =>
        Object.entries(this.filters).every(([group, value]) =>
          this.matchesValue(card.dataset[group] || '', value)
        )
      );
    }

    renderCards() {
      const matchingCards = this.getMatchingCards();
      const visibleCards = new Set(matchingCards.slice(0, this.visibleLimit));
      const matchingCount = matchingCards.length;
      const visibleCount = Math.min(this.visibleLimit, matchingCount);

      this.cards.forEach((card) => {
        card.hidden = !visibleCards.has(card);
      });

      const hasFilters = Object.values(this.filters).some((value) => value !== 'all');
      if (this.status) {
        this.status.textContent = matchingCount === visibleCount
          ? `${matchingCount} inspiration${matchingCount === 1 ? '' : 's'}`
          : `Showing ${visibleCount} of ${matchingCount} inspirations`;
      }
      if (this.clearButton) this.clearButton.hidden = !hasFilters;
      if (this.emptyState) this.emptyState.hidden = matchingCount !== 0;
      if (this.loadMoreWrap) this.loadMoreWrap.hidden = visibleCount >= matchingCount;
    }

    applySort() {
      if (!this.grid || !this.sortSelect) return;
      const direction = this.sortSelect.value;
      const sortedCards = [...this.cards].sort((cardA, cardB) => {
        if (direction === 'title-asc') return (cardA.dataset.cardTitle || '').localeCompare(cardB.dataset.cardTitle || '');
        const orderA = Number(cardA.dataset.cardOrder || 0);
        const orderB = Number(cardB.dataset.cardOrder || 0);
        return direction === 'oldest' ? orderB - orderA : orderA - orderB;
      });
      sortedCards.forEach((card) => this.grid.appendChild(card));
      this.cards = sortedCards;
    }

    resetFilters() {
      Object.keys(this.filters).forEach((groupName) => { this.filters[groupName] = 'all'; });
      this.groups.forEach((group) => {
        group.querySelectorAll('[data-filter-value]').forEach((option) => {
          option.setAttribute('aria-pressed', String(option.dataset.filterValue === 'all'));
        });
      });
      this.resetVisibleLimit();
      this.renderCards();
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback.apply(this, args), wait);
      };
    }
  }

  customElements.define('bbj-inspiration-library', BBJInspirationLibrary);
}
